import { useContext, useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Menu, Moon, Phone, Sun, X } from "lucide-react";
import { ThemeContext } from "@/App";
import Logo from "@/components/Logo";
import { FIRM, PRACTICE_AREAS, TEAM } from "@/data/content";

const ABOUT_ITEMS = [
  { to: "/about", label: "About Us", desc: "A boutique firm, built on purpose", testId: "nav-dropdown-about-us" },
  ...TEAM.map((m) => ({ to: `/about/${m.slug}`, label: m.name, desc: m.role, testId: `nav-dropdown-${m.id}` })),
];

const SERVICE_ITEMS = [
  ...PRACTICE_AREAS.map((a) => ({ to: `/services/${a.id}`, label: a.title, desc: a.tagline, num: a.num, testId: `nav-dropdown-${a.id}` })),
  { to: "/services", label: "All Areas of Practice", desc: "The full overview", testId: "nav-dropdown-all-services" },
];

const RESOURCE_ITEMS = [
  { to: "/blog", label: "Blog Posts", desc: "Field notes from the practice", testId: "nav-dropdown-blog" },
  { to: "/resources", label: "Quick Guides", desc: "Free downloadable Tennessee legal guides", testId: "nav-dropdown-guides" },
  { to: "/resources#events", label: "Upcoming Events", desc: "Seminars and training sessions", testId: "nav-dropdown-events" },
];

const triggerCls = (open) =>
  `gold-underline flex items-center gap-1.5 pb-1 text-sm tracking-wide transition-colors duration-300 ${
    open ? "text-gold" : "text-foreground/80 hover:text-foreground"
  }`;

const DROP_TO = { about: "/about", services: "/services", resources: "/resources" };

function NavDrop({ id, label, testId, items, openMenu, setOpenMenu }) {
  const open = openMenu === id;
  const { pathname } = useLocation();
  const to = DROP_TO[id];
  const inSection = pathname === to || pathname.startsWith(`${to}/`);
  return (
    <div
      className="relative"
      data-testid={`nav-${id}-dropdown`}
      onMouseEnter={() => setOpenMenu(id)}
      onMouseLeave={() => setOpenMenu(null)}
      onClick={(e) => e.stopPropagation()}
    >
      <NavLink
        to={to}
        data-testid={testId}
        aria-expanded={open}
        className={`gold-underline flex items-center gap-1.5 pb-1 text-sm tracking-wide transition-colors duration-300 ${
          open || inSection ? "text-gold" : "text-foreground/80 hover:text-foreground"
        }`}
      >
        {label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${open ? "rotate-180" : ""}`} strokeWidth={1.5} />
      </NavLink>
      <AnimatePresence>
        {open && (
          <div className="absolute left-1/2 top-full z-50 w-80 -translate-x-1/2 pt-4">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 6 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="border border-border bg-card/95 py-2 shadow-[0_24px_60px_-24px_rgba(0,0,0,0.55)] backdrop-blur-xl"
            >
              {items.map((item) => (
                <NavLink
                  key={item.testId}
                  to={item.to}
                  data-testid={item.testId}
                  onClick={() => setOpenMenu(null)}
                  className="group/item flex items-start gap-3 px-5 py-3 transition-colors duration-200 hover:bg-secondary"
                >
                  {item.num && <span className="mt-1 font-mono text-[10px] text-gold">{item.num}</span>}
                  <span>
                    <span className="block font-serif text-base font-medium leading-snug text-foreground transition-colors duration-200 group-hover/item:text-gold">
                      {item.label}
                    </span>
                    <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground line-clamp-1">{item.desc}</span>
                  </span>
                </NavLink>
              ))}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}

const MOBILE_GROUPS = {
  About: ABOUT_ITEMS,
  Services: SERVICE_ITEMS,
  Resources: RESOURCE_ITEMS,
};

const LINKS = [
  { to: "/", label: "Home", testId: "nav-home-link" },
  { label: "About", testId: "nav-about-link", dropId: "about" },
  { label: "Services", testId: "nav-services-link", dropId: "services" },
  { label: "Resources", testId: "nav-blog-link", dropId: "resources" },
  { to: "/contact", label: "Contact", testId: "nav-contact-link" },
];

const DROPDOWN_ITEMS = { about: ABOUT_ITEMS, services: SERVICE_ITEMS, resources: RESOURCE_ITEMS };

export default function Navbar() {
  const { theme, toggleTheme } = useContext(ThemeContext);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setOpenMenu(null);
  }, [pathname]);

  useEffect(() => {
    const close = () => setOpenMenu(null);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, []);

  return (
    <header
      data-testid="nav-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-background/80 backdrop-blur-xl border-b border-border" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-8 lg:px-16">
        <Link to="/" data-testid="nav-logo" className="group flex items-center">
          <Logo className="h-10 w-auto transition-transform duration-300 group-hover:scale-[1.02] sm:h-11 lg:h-[4.25rem]" />
        </Link>

        <nav className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) =>
            l.dropId ? (
              <NavDrop
                key={l.label}
                id={l.dropId}
                label={l.label}
                testId={l.testId}
                items={DROPDOWN_ITEMS[l.dropId]}
                openMenu={openMenu}
                setOpenMenu={setOpenMenu}
              />
            ) : (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testId}
                className={({ isActive }) =>
                  `gold-underline pb-1 text-sm tracking-wide transition-colors duration-300 ${
                    isActive ? "text-gold" : "text-foreground/80 hover:text-foreground"
                  }`
                }
              >
                {l.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <a
            href={FIRM.phoneHref}
            data-testid="quick-call-btn"
            className="hidden items-center gap-2 border border-border px-4 py-2 text-xs tracking-wide text-foreground/90 transition-all duration-300 hover:border-gold hover:text-gold md:flex"
          >
            <Phone className="h-3.5 w-3.5" strokeWidth={1.5} />
            {FIRM.phone}
          </a>
          <a
            href={FIRM.phoneHref}
            data-testid="mobile-call-icon-btn"
            aria-label="Call us"
            className="flex h-9 w-9 items-center justify-center border border-border text-foreground/80 transition-all duration-300 hover:border-gold hover:text-gold md:hidden"
          >
            <Phone className="h-4 w-4" strokeWidth={1.5} />
          </a>
          <button
            data-testid="theme-toggle-btn"
            onClick={toggleTheme}
            aria-label="Toggle theme"
            className="flex h-9 w-9 items-center justify-center border border-border text-foreground/80 transition-all duration-300 hover:border-gold hover:text-gold"
          >
            {theme === "dark" ? <Sun className="h-4 w-4" strokeWidth={1.5} /> : <Moon className="h-4 w-4" strokeWidth={1.5} />}
          </button>
          <button
            data-testid="nav-consultation-btn"
            onClick={() => window.dispatchEvent(new CustomEvent("hlt:open-booking"))}
            className="hidden cursor-pointer bg-gold px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] text-primary-foreground transition-all duration-300 hover:brightness-110 sm:block"
          >
            Free Consultation
          </button>
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            className="flex h-9 w-9 items-center justify-center border border-border lg:hidden"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-b border-border bg-background/95 backdrop-blur-xl lg:hidden"
          >
            <div data-lenis-prevent className="flex max-h-[70vh] flex-col gap-1 overflow-y-auto px-6 py-6">
              {LINKS.map((l, i) => (
                <motion.div
                  key={l.label}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                >
                  {l.dropId ? (
                    <>
                      <span className="block py-3 font-serif text-2xl text-foreground/85">{l.label}</span>
                      <div className="mb-2 ml-1 border-l border-border pl-5">
                        {MOBILE_GROUPS[l.label].map((item) => (
                          <NavLink
                            key={item.testId}
                            to={item.to}
                            data-testid={`mobile-${item.testId}`}
                            className="block py-2 text-sm text-muted-foreground transition-colors hover:text-gold"
                          >
                            {item.num && <span className="mr-2 font-mono text-[10px] text-gold">{item.num}</span>}
                            {item.label}
                          </NavLink>
                        ))}
                      </div>
                    </>
                  ) : (
                    <NavLink
                      to={l.to}
                      data-testid={l.testId}
                      className={({ isActive }) =>
                        `block py-3 font-serif text-2xl ${isActive ? "text-gold" : "text-foreground/85"}`
                      }
                    >
                      {l.label}
                    </NavLink>
                  )}
                </motion.div>
              ))}
              <a href={FIRM.phoneHref} className="mt-4 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-gold">
                <Phone className="h-3.5 w-3.5" /> {FIRM.phone}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
